import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { leadApi } from '@/services/api';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';

export interface AgentOption {
  user_id: string;
  full_name: string;
  can_take_leads?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leadId: string | null;
  customerName?: string;
  currentAgentId?: string | null;
  currentAgentName?: string | null;
  agents: AgentOption[];
  onSuccess?: () => void;
}

export function ReassignLeadDialog({ open, onOpenChange, leadId, customerName, currentAgentId, currentAgentName, agents, onSuccess }: Props) {
  const [toAgent, setToAgent] = useState<string>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const eligible = agents.filter(a => a.can_take_leads !== false && a.user_id !== currentAgentId);

  const submit = async () => {
    if (!leadId || !toAgent) return;
    if (reason.trim().length < 5) {
      toast.error('Please enter a reason (min 5 characters)');
      return;
    }
    setBusy(true);
    try {
      await leadApi.reassign(leadId, toAgent, reason.trim());
      toast.success('Lead reassigned');
      onOpenChange(false);
      setToAgent('');
      setReason('');
      onSuccess?.();
    } catch (e: any) {
      toast.error(e.message || 'Failed to reassign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reassign Lead</DialogTitle>
          <DialogDescription>
            {customerName ? `Reassign ${customerName}'s inquiry` : 'Reassign this inquiry'}
            {currentAgentName ? ` away from ${currentAgentName}.` : '.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>Both the original agent and the new agent will be notified. The action and reason are logged in the lead's history.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">Reassign to</Label>
            <Select value={toAgent} onValueChange={setToAgent}>
              <SelectTrigger><SelectValue placeholder="Pick an agent" /></SelectTrigger>
              <SelectContent>
                {eligible.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No eligible agents</div>
                )}
                {eligible.map(a => (
                  <SelectItem key={a.user_id} value={a.user_id}>{a.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">Reason (required)</Label>
            <Textarea
              placeholder="e.g. Agent on leave, customer requested a different agent, etc."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
            />
            <div className="text-[10px] text-muted-foreground text-right">{reason.length}/500</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !toAgent}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Reassign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
